import React, { useRef, useState, useEffect, Suspense, useCallback, useMemo } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { Box, Edges, OrbitControls, Sphere, Shape, Extrude } from '@react-three/drei';
import { Physics, RigidBody, type RapierRigidBody } from '@react-three/rapier';
import * as THREE from 'three';

// --- Constants ---
const CUBE_SIZE = 0.6;
const LEG_SPACING = CUBE_SIZE * 1.5;
const TORSO_Y = CUBE_SIZE * 2.5;
const HEAD_Y = TORSO_Y + CUBE_SIZE * 1.2;
const ARM_Y = TORSO_Y;
const ARM_X = CUBE_SIZE * 1.1;
const LEG_Y = CUBE_SIZE * 0.8;
const EYE_OFFSET_X = CUBE_SIZE * 0.2;
const EYE_OFFSET_Z = CUBE_SIZE * 0.51; // Slightly forward
const EYE_SIZE = CUBE_SIZE * 0.18;
const PUPIL_SIZE = EYE_SIZE * 0.5;
const PUPIL_OFFSET_Z = EYE_SIZE * 0.51; // Slightly forward from eye surface
const EYEBROW_Y = EYE_SIZE * 0.9;
const EYEBROW_Z = EYE_OFFSET_Z;
const EYEBROW_WIDTH = EYE_SIZE * 1.5;
const EYEBROW_HEIGHT = EYE_SIZE * 0.3;
const EYEBROW_DEPTH = EYE_SIZE * 0.2;

// Baby character constants
const BABY_SCALE = 0.6; // Slightly smaller scale for baby cubes
const BABY_CUBE_SIZE = CUBE_SIZE * 1.5; // Increased size for baby cube
const BABY_JUMP_HEIGHT = 1.0; // How high the baby jumps when clicked
const BABY_JUMP_DURATION = 0.5; // How long the jump animation takes (seconds)

const FALLING_CUBE_LIFESPAN = 3000;
const MAX_FALLING_CUBES = 30;
const TAP_SHED_COUNT = 3;
const GRAVITY_Y = -12;
const KISS_TRIGGER_COUNT = 10;
const CHARACTER_SPACING = 2.5;
const GOLD_COLOR = "#FFD700"; // Brighter gold
const RED_COLOR = "#FF0000"; // Bright red
const ORANGE_COLOR = "#FF6347"; // Reddish orange for baby
const PINK_COLOR = "#f9a8d4"; // Tailwind pink-300
const CLOUD_COLOR = "#ffffff";
const CLOUD_OUTLINE_COLOR = "#000000";

// Character Actions
type CharacterAction =
  | 'walking' | 'dancing' | 'jumping' | 'sitting' | 'running_circles'
  | 'rotating' | 'twirling' | 'thinking' | 'hands_up' | 'jiggling' // Gold actions
  | 'somersault' | 'cartwheel' | 'body_roll' | 'wave' | 'spin_kick' | 'shuffle' // Red actions
  | 'kiss_dance' | 'break_dance'; // Shared actions

const GOLD_ACTIONS: CharacterAction[] = ['walking', 'dancing', 'jumping', 'sitting', 'running_circles', 'rotating', 'twirling', 'thinking', 'hands_up', 'jiggling'];
const RED_ACTIONS: CharacterAction[] = ['flying', 'body_roll', 'wave', 'yoga', 'shimmy', 'hover_spin'];

// Tap prompts
const TAP_PROMPTS = [
  "Tap us!", "Tap tap!", "Boop!", "Tap again!", "We like taps!",
  "Tap for fun!", "Poke us!", "Yay, taps!", "Keep tapping!", "Almost there...",
];
const KISS_PROMPT = "Smooch! <3";
const DANCE_PROMPT = "Dance time!";
const BREAK_DANCE_PROMPT = "Break it down!";
const CONTINUE_PROMPT = "Tap to continue...";

// Helper function to generate random vectors
const randomVector = (magnitude: number) => new THREE.Vector3(
  (Math.random() - 0.5) * 2 * magnitude,
  (Math.random() - 0.5) * 2 * magnitude,
  (Math.random() - 0.5) * 2 * magnitude
);

// Represents a single falling cube
interface FallingCubeData {
  id: number;
  position: THREE.Vector3;
  rotation: THREE.Quaternion;
  initialVelocity: THREE.Vector3;
  spawnTime: number;
  color: string; // Added color property
}

  // Baby Cube Component with jump animation
function BabyCube({ 
  position, 
  size, 
  scale, 
  lookDirection,
  isNewest
}: { 
  position: THREE.Vector3, 
  size: number, 
  scale: number,
  lookDirection: THREE.Vector3,
  isNewest: boolean
}) {
  const cubeRef = useRef<THREE.Group>(null);
  const [isJumping, setIsJumping] = useState(false);
  const jumpStartTime = useRef(0);
  const jumpHeight = useRef(0);
  const initialY = useRef(position.y).current;
  
  useFrame((state) => {
    if (!isJumping || !cubeRef.current) return;
    
    const elapsed = state.clock.elapsedTime - jumpStartTime.current;
    const duration = BABY_JUMP_DURATION;
    const progress = Math.min(elapsed / duration, 1);
    
    // Jump up and down with a parabolic curve
    const jumpCurve = Math.sin(progress * Math.PI);
    const height = jumpCurve * jumpHeight.current;
    
    // Rotate during jump
    const rotation = progress * Math.PI * 2;
    
    // Ensure we never go below the initial position (ground level)
    cubeRef.current.position.y = Math.max(initialY, initialY + height);
    cubeRef.current.rotation.y = rotation;
    
    // End jump when complete
    if (progress >= 1) {
      setIsJumping(false);
      cubeRef.current.position.y = initialY; // Return to initial position
      cubeRef.current.rotation.y = 0; // Reset rotation
    }
  });
  
  const handleClick = (e: THREE.Event) => {
    e.stopPropagation();
    if (!isJumping) {
      setIsJumping(true);
      jumpStartTime.current = performance.now() / 1000; // Convert to seconds for consistency
      jumpHeight.current = BABY_JUMP_HEIGHT;
      window.gizmoRuntime.performHaptic('medium');
    }
  };
  
  return (
    <group 
      ref={cubeRef} 
      position={position.toArray()} 
      scale={[scale, scale, scale]}
    >
      <mesh castShadow onClick={handleClick}>
        <boxGeometry args={[size, size, size]} />
        <meshStandardMaterial color={ORANGE_COLOR} />
        <Edges scale={1.02} threshold={15} color="black" />
      </mesh>
      {/* Extra big eyes for baby cubes */}
      <GooglyEye
        position={[-size * 0.25, size * 0.1, size * 0.51]}
        lookDirection={lookDirection}
        eyeSize={EYE_SIZE * 1.5} // Bigger eyes than main characters
        pupilSize={PUPIL_SIZE * 2.3} // Slightly smaller pupil/iris for orange cubes
      />
      <GooglyEye
        position={[size * 0.25, size * 0.1, size * 0.51]}
        lookDirection={lookDirection}
        eyeSize={EYE_SIZE * 1.5} // Bigger eyes than main characters
        pupilSize={PUPIL_SIZE * 2.3} // Slightly smaller pupil/iris for orange cubes
      />
    </group>
  );
}

// Component for a single falling cube with physics
function FallingCube({ id, position, rotation, initialVelocity, spawnTime, color, onDespawn }: FallingCubeData & { onDespawn: (id: number) => void }) {
  const rigidBodyRef = useRef<RapierRigidBody>(null);
  const restitution = 0.3;

  useEffect(() => {
    if (rigidBodyRef.current) {
      rigidBodyRef.current.setLinvel(initialVelocity, true);
      rigidBodyRef.current.setAngvel(randomVector(1.5), true);
    }
  }, [initialVelocity]);

  useEffect(() => {
    const timeToLive = FALLING_CUBE_LIFESPAN - (Date.now() - spawnTime);
    const timer = setTimeout(() => {
      onDespawn(id);
    }, Math.max(0, timeToLive));
    return () => clearTimeout(timer);
  }, [id, onDespawn, spawnTime]);

  return (
    <RigidBody
      ref={rigidBodyRef}
      colliders="cuboid"
      position={position.toArray()}
      rotation={new THREE.Euler().setFromQuaternion(rotation).toArray().slice(0, 3) as [number, number, number]}
      restitution={restitution}
      linearDamping={0.3}
      angularDamping={0.8}
      canSleep={true}
    >
      <Box args={[CUBE_SIZE, CUBE_SIZE, CUBE_SIZE]} castShadow receiveShadow>
        <meshStandardMaterial color={color} />
        <Edges scale={1.02} threshold={15} color="black" />
      </Box>
    </RigidBody>
  );
}

// Googly Eye Component
function GooglyEye({ 
  position, 
  lookDirection, 
  eyeSize = EYE_SIZE, 
  pupilSize = PUPIL_SIZE 
}: { 
  position: [number, number, number], 
  lookDirection: THREE.Vector3,
  eyeSize: number,
  pupilSize: number
}) {
  const eyeRef = useRef<THREE.Group>(null);
  const pupilRef = useRef<THREE.Mesh>(null);

  // Use default values if not provided
  const currentEyeSize = eyeSize ?? EYE_SIZE;
  const currentPupilSize = pupilSize ?? PUPIL_SIZE;

  useFrame(() => {
    if (eyeRef.current && pupilRef.current) {
      const maxPupilOffset = currentEyeSize * 0.5 - currentPupilSize * 0.5;
      const pupilX = Math.max(-maxPupilOffset, Math.min(maxPupilOffset, lookDirection.x * maxPupilOffset * 1.5));
      const pupilY = Math.max(-maxPupilOffset, Math.min(maxPupilOffset, lookDirection.y * maxPupilOffset * 1.5));
      pupilRef.current.position.set(pupilX, pupilY, PUPIL_OFFSET_Z);
    }
  });

  return (
    <group ref={eyeRef} position={position}>
      <Sphere args={[currentEyeSize, 16, 16]}>
        <meshStandardMaterial color="white" />
      </Sphere>
      <mesh ref={pupilRef} position={[0, 0, PUPIL_OFFSET_Z]}>
        <Sphere args={[currentPupilSize * 1.1, 12, 12]}> {/* Slightly reduced pupil size */}
          <meshStandardMaterial color="black" />
        </Sphere>
      </mesh>
    </group>
  );
}

// Eyebrow Component
function Eyebrow({ position }: { position: [number, number, number] }) {
  return (
    <mesh position={position}>
      <boxGeometry args={[EYEBROW_WIDTH, EYEBROW_HEIGHT, EYEBROW_DEPTH]} />
      <meshStandardMaterial color="black" />
    </mesh>
  );
}

// Heart Shape for Clouds (Low Poly)
function createLowPolyHeartShape() {
  const shape = new THREE.Shape();
  const s = 0.8; // Scale factor for size
  shape.moveTo(0 * s, -0.5 * s);
  shape.lineTo(-0.6 * s, 0.3 * s);
  shape.lineTo(-0.3 * s, 0.7 * s);
  shape.lineTo(0 * s, 0.4 * s);
  shape.lineTo(0.3 * s, 0.7 * s);
  shape.lineTo(0.6 * s, 0.3 * s);
  shape.lineTo(0 * s, -0.5 * s);
  return shape;
}

const lowPolyHeartShape = createLowPolyHeartShape();
const extrudeSettingsLowPoly = { depth: 0.1, bevelEnabled: false };

// Cloud Component (Low Poly)
function Cloud({ 
  initialPosition, 
  speed, 
  isPaused = false,
  isKissing = false
}: { 
  initialPosition: [number, number, number], 
  speed: number,
  isPaused?: boolean,
  isKissing?: boolean
}) {
  const cloudRef = useRef<THREE.Group>(null);
  const { viewport } = useThree();
  const cloudWidth = 1.5; // Approximate width of the low poly heart cloud
  const originalPosition = useRef(new THREE.Vector3(...initialPosition)).current;
  const baseScale = 1.2; // Consistent scale for all clouds
  const [cloudColor, setCloudColor] = useState(CLOUD_COLOR);
  const DARK_PINK = "#e75480"; // Dark pink color for hearts during kiss

  // Handle color transition
  useEffect(() => {
    let transitionTimer: NodeJS.Timeout;
    if (isKissing) {
      // Gradually change to dark pink over 1 second
      transitionTimer = setTimeout(() => {
        setCloudColor(DARK_PINK);
      }, 1000);
    } else {
      // Gradually change back to white
      setCloudColor(CLOUD_COLOR);
    }
    return () => clearTimeout(transitionTimer);
  }, [isKissing]);

  useFrame((state, delta) => {
    if (cloudRef.current) {
      // Only move clouds if not paused
      if (!isPaused) {
        cloudRef.current.position.x += speed * delta;
        // Reset position when it goes off-screen right
        if (cloudRef.current.position.x > viewport.width / 2 + cloudWidth) {
          cloudRef.current.position.x = -viewport.width / 2 - cloudWidth;
          // Optional: Randomize Y position slightly on reset
          cloudRef.current.position.y = originalPosition.y + (Math.random() - 0.5) * 0.5;
        }
      }
    }
  });

  return (
    <group ref={cloudRef} position={initialPosition} rotation={[0, 0, 0]} scale={[baseScale, baseScale, baseScale]}>
      <Extrude args={[lowPolyHeartShape, extrudeSettingsLowPoly]} castShadow>
        <meshStandardMaterial color={cloudColor} flatShading={true} />
      </Extrude>
      {/* Outline using Edges */}
      <Edges scale={1.01} threshold={1} color={CLOUD_OUTLINE_COLOR} />
    </group>
  );
}


// Kiss Animation State
type KissPhase = 'movingTo' | 'kissing' | 'returning' | null;
type PostKissState = 'idle' | 'dancing' | 'break_dancing'; // Added break_dancing
type SkyColor = 'blue' | 'pink';

// The main character component (Refactored)
function Character({
  id,
  color,
  initialPosition,
  onCharacterTap,
  action,
  lookDirection,
  isKissing,
  kissTargetPosition,
  returnToPosition,
  kissPhase,
  postKissState,
}: {
  id: string;
  color: string;
  initialPosition: [number, number, number];
  onCharacterTap: (id: string, position: THREE.Vector3, color: string, currentPhase: KissPhase) => void; // Added currentPhase
  action: CharacterAction;
  lookDirection: THREE.Vector3;
  isKissing: boolean;
  kissTargetPosition?: THREE.Vector3;
  returnToPosition?: THREE.Vector3;
  kissPhase?: KissPhase;
  postKissState: PostKissState;
}) {
  const characterGroupRef = useRef<THREE.Group>(null);
  const leftLegRef = useRef<THREE.Mesh>(null);
  const rightLegRef = useRef<THREE.Mesh>(null);
  const torsoRef = useRef<THREE.Mesh>(null);
  const headRef = useRef<THREE.Mesh>(null);
  const leftArmRef = useRef<THREE.Mesh>(null);
  const rightArmRef = useRef<THREE.Mesh>(null);
  const leftEyebrowRef = useRef<THREE.Mesh>(null);
  const rightEyebrowRef = useRef<THREE.Mesh>(null);

  const initialRotation = useRef(new THREE.Euler()).current;
  const kissTargetRotation = useRef(new THREE.Euler(0, id === 'gold' ? Math.PI / 8 : -Math.PI / 8, 0)).current; // Face each other slightly

  // Animation Logic
  useFrame((state, delta) => {
    const time = state.clock.getElapsedTime();
    const group = characterGroupRef.current;
    if (!group) return;

  // --- Kiss Animation ---
    if (isKissing && kissPhase) {
      const speed = 3.0;
      let targetPos: THREE.Vector3 | undefined;
      let targetRot: THREE.Euler = initialRotation;

      if (kissPhase === 'movingTo' && kissTargetPosition) {
        targetPos = kissTargetPosition;
        targetRot = kissTargetRotation;
      } else if (kissPhase === 'returning' && returnToPosition) {
        // Faster return speed when breaking the kiss
        const returnSpeed = 8.0; // Increased for faster return
        targetPos = returnToPosition;
        targetRot = initialRotation;
        if (targetPos) group.position.lerp(targetPos, returnSpeed * delta);
        const targetQuat = new THREE.Quaternion().setFromEuler(targetRot);
        group.quaternion.slerp(targetQuat, returnSpeed * delta);
        
        if (group.position.distanceTo(returnToPosition) < 0.05) {
          group.position.copy(returnToPosition);
          group.quaternion.setFromEuler(initialRotation);
        }
        
        if (headRef.current) {
          headRef.current.rotation.z = 0;
        }
        return;
      } else if (kissPhase === 'kissing') {
        targetPos = kissTargetPosition; // Stay at kiss spot
        targetRot = kissTargetRotation;
        if (headRef.current) {
            // Fixed head tilt during kiss - no animation
            headRef.current.rotation.z = (id === 'gold' ? 0.15 : -0.15);
        }
      }

      if (targetPos && kissPhase !== 'returning') group.position.lerp(targetPos, speed * delta);
      if (kissPhase !== 'returning') {
        const targetQuat = new THREE.Quaternion().setFromEuler(targetRot);
        group.quaternion.slerp(targetQuat, speed * delta);
      }

      if (kissPhase !== 'kissing' && headRef.current) {
          headRef.current.rotation.z = 0;
      }
      // Don't continue with other animations during any kiss phase
      return;
    } else {
       if (headRef.current) headRef.current.rotation.z = 0;
       if (!isKissing && returnToPosition && group.position.distanceTo(returnToPosition) > 0.01) {
           group.position.copy(returnToPosition);
           group.quaternion.setFromEuler(initialRotation);
       }
    }

    // --- Regular & Post-Kiss Animation Logic ---
    const refs = { leftLegRef, rightLegRef, torsoRef, headRef, leftArmRef, rightArmRef, leftEyebrowRef, rightEyebrowRef };

    // Reset rotations/positions unless it's a specific animation type that modifies the whole group
    if (postKissState !== 'break_dancing' && action !== 'somersault' && action !== 'cartwheel' && action !== 'running_circles' && action !== 'rotating' && action !== 'twirling' && action !== 'kiss_dance') {
        group.rotation.set(0,0,0);
        Object.values(refs).forEach(ref => {
          if (ref.current) {
            ref.current.rotation.set(0, 0, 0);
            ref.current.position.set(0,0,0);
          }
        });
        if (leftLegRef.current) leftLegRef.current.position.set(-LEG_SPACING / 2, LEG_Y, 0);
        if (rightLegRef.current) rightLegRef.current.position.set(LEG_SPACING / 2, LEG_Y, 0);
        if (torsoRef.current) torsoRef.current.position.set(0, TORSO_Y, 0);
        if (headRef.current) headRef.current.position.set(0, HEAD_Y, 0);
        if (leftArmRef.current) leftArmRef.current.position.set(-ARM_X, ARM_Y, 0);
        if (rightArmRef.current) rightArmRef.current.position.set(ARM_X, ARM_Y, 0);
        if (leftEyebrowRef.current) leftEyebrowRef.current.position.set(-EYE_OFFSET_X, EYEBROW_Y, EYEBROW_Z);
        if (rightEyebrowRef.current) rightEyebrowRef.current.position.set(EYE_OFFSET_X, EYEBROW_Y, EYEBROW_Z);
    }


    // Determine current action based on post-kiss state
    let currentAction: CharacterAction = action;
    if (postKissState === 'dancing') currentAction = 'kiss_dance';
    else if (postKissState === 'break_dancing') currentAction = 'break_dance';


    // Apply action-specific animation
    switch (currentAction) {
      case 'break_dance':
        const breakTime = time * 5;
        const spinSpeed = 4;
        const kickAmp = Math.PI / 2.5;
        const floorSpin = Math.sin(breakTime * 0.5) > 0; // Alternate between floor spin and kick

        if (floorSpin) {
            // Floor spin
            group.position.y = initialPosition[1] + CUBE_SIZE * 0.5; // Lower to ground
            group.rotation.x = Math.PI / 2; // Lie flat
            group.rotation.z += spinSpeed * delta * 2; // Spin faster
            if (leftLegRef.current) leftLegRef.current.rotation.y = Math.sin(breakTime * 2) * 0.5;
            if (rightLegRef.current) rightLegRef.current.rotation.y = Math.sin(breakTime * 2 + Math.PI) * 0.5;
            if (leftArmRef.current) leftArmRef.current.rotation.y = Math.PI / 3;
            if (rightArmRef.current) rightArmRef.current.rotation.y = -Math.PI / 3;
        } else {
            // Kick / Freeze pose
            group.position.y = initialPosition[1];
            group.rotation.x = 0;
            group.rotation.z = 0;
            group.rotation.y += spinSpeed * delta * 0.5; // Slower spin
            if (leftLegRef.current) leftLegRef.current.rotation.x = -kickAmp * 0.8;
            if (rightLegRef.current) rightLegRef.current.rotation.x = -kickAmp;
            if (leftArmRef.current) leftArmRef.current.rotation.z = Math.PI / 3;
            if (rightArmRef.current) rightArmRef.current.rotation.z = -Math.PI / 3;
            if (torsoRef.current) torsoRef.current.rotation.x = -0.2;
        }
        break;
      case 'kiss_dance':
        const danceTime = time * 4;
        const sway = Math.sin(danceTime) * 0.15;
        const hop = Math.abs(Math.sin(danceTime * 0.5)) * 0.1;
        group.position.y = initialPosition[1] + hop;
        group.rotation.y = sway * 0.5;
        if (torsoRef.current) torsoRef.current.rotation.z = sway;
        if (headRef.current) headRef.current.rotation.z = -sway * 0.8;
        if (leftArmRef.current) leftArmRef.current.rotation.z = Math.PI / 4 + Math.sin(danceTime + 1) * 0.2;
        if (rightArmRef.current) rightArmRef.current.rotation.z = -Math.PI / 4 + Math.sin(danceTime + 1.5) * 0.2;
        break;
      // --- Gold Character Actions ---
      case 'walking':
        const walkAngle = time * 3; const walkAmpLeg = 0.5;
        if (leftLegRef.current) leftLegRef.current.rotation.x = Math.sin(walkAngle) * walkAmpLeg;
        if (rightLegRef.current) rightLegRef.current.rotation.x = Math.sin(walkAngle + Math.PI) * walkAmpLeg;
        if (leftArmRef.current) leftArmRef.current.rotation.x = Math.sin(walkAngle + Math.PI) * walkAmpLeg * 0.7;
        if (rightArmRef.current) rightArmRef.current.rotation.x = Math.sin(walkAngle) * walkAmpLeg * 0.7;
        break;
      case 'dancing':
        const danceAngle = time * 5; const danceAmp = 0.3;
        if (torsoRef.current) torsoRef.current.rotation.z = Math.sin(danceAngle) * danceAmp * 0.5;
        if (headRef.current) headRef.current.rotation.y = Math.sin(danceAngle * 1.5) * danceAmp;
        if (leftArmRef.current) leftArmRef.current.rotation.z = Math.sin(danceAngle + Math.PI * 0.5) * danceAmp * 2;
        if (rightArmRef.current) rightArmRef.current.rotation.z = Math.sin(danceAngle) * danceAmp * 2;
        break;
      case 'jumping':
        const jumpAngle = time * 4; const jumpHeight = Math.abs(Math.sin(jumpAngle)) * 0.8;
        group.position.y = initialPosition[1] + jumpHeight; // Add jump height to initial Y
        const bend = Math.sin(jumpAngle + Math.PI * 0.5) * 0.2 + 0.2;
        if (leftLegRef.current) leftLegRef.current.rotation.x = -bend;
        if (rightLegRef.current) rightLegRef.current.rotation.x = -bend;
        if (leftArmRef.current) leftArmRef.current.rotation.x = bend * 1.5;
        if (rightArmRef.current) rightArmRef.current.rotation.x = bend * 1.5;
        break;
      case 'sitting':
         group.position.y = initialPosition[1] - CUBE_SIZE * 0.5; // Adjust group position for sit
         if (torsoRef.current) torsoRef.current.position.y = TORSO_Y - CUBE_SIZE * 0.5;
         if (headRef.current) headRef.current.position.y = HEAD_Y - CUBE_SIZE * 0.5;
         if (leftArmRef.current) { leftArmRef.current.position.y = ARM_Y - CUBE_SIZE * 0.5; leftArmRef.current.rotation.x = -Math.PI / 6; leftArmRef.current.rotation.z = Math.PI / 8; }
         if (rightArmRef.current) { rightArmRef.current.position.y = ARM_Y - CUBE_SIZE * 0.5; rightArmRef.current.rotation.x = -Math.PI / 6; rightArmRef.current.rotation.z = -Math.PI / 8; }
         if (leftLegRef.current) { leftLegRef.current.position.y = LEG_Y - CUBE_SIZE * 0.5; leftLegRef.current.position.z = CUBE_SIZE * 0.5; leftLegRef.current.rotation.x = -Math.PI / 2.2; }
         if (rightLegRef.current) { rightLegRef.current.position.y = LEG_Y - CUBE_SIZE * 0.5; rightLegRef.current.position.z = CUBE_SIZE * 0.5; rightLegRef.current.rotation.x = -Math.PI / 2.2; }
         break;
      case 'running_circles':
        const runCircleAngle = time * 2; const runCircleRadius = 1.5;
        group.position.x = initialPosition[0] + Math.sin(runCircleAngle) * runCircleRadius;
        group.position.z = initialPosition[2] + Math.cos(runCircleAngle) * runCircleRadius;
        group.rotation.y = -runCircleAngle + Math.PI / 2;
        const runAngle = time * 8; const runAmpLeg = 0.6;
        if (leftLegRef.current) leftLegRef.current.rotation.x = Math.sin(runAngle) * runAmpLeg;
        if (rightLegRef.current) rightLegRef.current.rotation.x = Math.sin(runAngle + Math.PI) * runAmpLeg;
        if (leftArmRef.current) leftArmRef.current.rotation.x = Math.sin(runAngle + Math.PI) * runAmpLeg * 0.8;
        if (rightArmRef.current) rightArmRef.current.rotation.x = Math.sin(runAngle) * runAmpLeg * 0.8;
        break;
      case 'rotating':
        group.rotation.y = time * 1.5;
        break;
      case 'twirling':
        const twirlSpeed = 4;
        group.rotation.y = time * twirlSpeed;
        if (leftArmRef.current) leftArmRef.current.rotation.z = Math.PI / 2.5;
        if (rightArmRef.current) rightArmRef.current.rotation.z = -Math.PI / 2.5;
        break;
      case 'thinking':
        if (headRef.current) headRef.current.rotation.x = Math.sin(time * 0.8) * 0.1 - 0.1;
        if (headRef.current) headRef.current.rotation.y = Math.sin(time * 0.6) * 0.2;
        if (rightArmRef.current) { rightArmRef.current.position.set(ARM_X * 0.5, ARM_Y - CUBE_SIZE * 0.3, CUBE_SIZE * 0.4); rightArmRef.current.rotation.x = -Math.PI / 3; rightArmRef.current.rotation.z = -Math.PI / 6; }
        if (leftEyebrowRef.current) leftEyebrowRef.current.rotation.z = 0.2;
        if (rightEyebrowRef.current) rightEyebrowRef.current.rotation.z = -0.2;
        break;
      case 'hands_up':
        const handsUpAngle = Math.PI * 0.8;
        if (leftArmRef.current) leftArmRef.current.rotation.z = handsUpAngle;
        if (rightArmRef.current) rightArmRef.current.rotation.z = -handsUpAngle;
        const bounce = Math.sin(time * 5) * 0.1;
        group.position.y = initialPosition[1] + bounce;
        if (leftEyebrowRef.current) leftEyebrowRef.current.position.y = EYEBROW_Y + 0.05;
        if (rightEyebrowRef.current) rightEyebrowRef.current.position.y = EYEBROW_Y + 0.05;
        break;
      case 'jiggling':
        const jiggleFreq = 15; const jiggleAmp = 0.05;
        if (torsoRef.current) torsoRef.current.position.x = Math.sin(time * jiggleFreq) * jiggleAmp;
        if (headRef.current) headRef.current.position.x = Math.sin(time * jiggleFreq + 0.5) * jiggleAmp * 1.2;
        if (leftArmRef.current) leftArmRef.current.position.x = -ARM_X + Math.sin(time * jiggleFreq + 1.0) * jiggleAmp;
        if (rightArmRef.current) rightArmRef.current.position.x = ARM_X + Math.sin(time * jiggleFreq + 1.5) * jiggleAmp;
        break;

      // --- Red Character Actions ---
      case 'flying':
        const flyHeight = Math.sin(time * 2) * 0.8;
        group.position.y = initialPosition[1] + flyHeight;
        if (leftArmRef.current) leftArmRef.current.rotation.z = Math.sin(time * 3) * 0.3 + 0.3;
        if (rightArmRef.current) rightArmRef.current.rotation.z = Math.sin(time * 3) * 0.3 - 0.3;
        break;
      case 'body_roll':
        const rollTime = time * 4; const rollAmp = 0.15;
        if (headRef.current) headRef.current.position.x = Math.sin(rollTime) * rollAmp;
        if (torsoRef.current) torsoRef.current.position.x = Math.sin(rollTime - 0.5) * rollAmp * 0.8;
        if (headRef.current) headRef.current.rotation.z = Math.sin(rollTime) * rollAmp * 0.5;
        if (torsoRef.current) torsoRef.current.rotation.z = Math.sin(rollTime - 0.5) * rollAmp * 0.4;
        break;
      case 'wave':
        if (rightArmRef.current) {
            rightArmRef.current.rotation.z = -Math.PI * 0.7;
            rightArmRef.current.rotation.x = Math.sin(time * 6) * 0.4;
        }
        break;
      case 'yoga':
        // Tree pose
        if (leftLegRef.current) {
          leftLegRef.current.rotation.z = Math.PI * 0.2;
          leftLegRef.current.position.y = LEG_Y + 0.1;
        }
        if (rightLegRef.current) rightLegRef.current.position.y = LEG_Y - 0.1;
        if (leftArmRef.current) leftArmRef.current.rotation.z = Math.PI * 0.5;
        if (rightArmRef.current) rightArmRef.current.rotation.z = -Math.PI * 0.5;
        if (torsoRef.current) torsoRef.current.rotation.x = 0.1;
        if (headRef.current) headRef.current.rotation.x = -0.1;
        break;
      case 'shimmy':
        const shimmyTime = time * 8; const shimmyAmp = 0.1;
        if (torsoRef.current) torsoRef.current.position.x = Math.sin(shimmyTime) * shimmyAmp;
        if (headRef.current) headRef.current.position.x = Math.sin(shimmyTime + 0.2) * shimmyAmp;
        if (leftArmRef.current) leftArmRef.current.rotation.z = Math.sin(shimmyTime) * 0.2 + 0.3;
        if (rightArmRef.current) rightArmRef.current.rotation.z = Math.sin(shimmyTime) * 0.2 - 0.3;
        if (leftLegRef.current) leftLegRef.current.position.x = -LEG_SPACING / 2 + Math.sin(shimmyTime) * shimmyAmp;
        if (rightLegRef.current) rightLegRef.current.position.x = LEG_SPACING / 2 + Math.sin(shimmyTime) * shimmyAmp;
        break;
      case 'hover_spin':
        const hoverHeight = Math.sin(time * 1.5) * 0.3;
        group.position.y = initialPosition[1] + hoverHeight;
        group.rotation.y = time * 2;
        if (leftArmRef.current) leftArmRef.current.rotation.z = Math.PI * 0.3;
        if (rightArmRef.current) rightArmRef.current.rotation.z = -Math.PI * 0.3;
        break;
    }
  });

  // Handle tap/click event
  const handleTap = useCallback((event: THREE.Event) => {
    event.stopPropagation();
    window.gizmoRuntime.performHaptic('medium');
    const mesh = event.object as THREE.Mesh;
    const worldPos = new THREE.Vector3();
    mesh.getWorldPosition(worldPos);
    // Pass the current kissPhase to the handler
    onCharacterTap(id, worldPos, color, kissPhase ?? null);
  }, [onCharacterTap, id, color, kissPhase]); // Include kissPhase

  return (
    <group ref={characterGroupRef} position={initialPosition}>
      {/* Head */}
      <mesh ref={headRef} position={[0, HEAD_Y, 0]} onClick={handleTap} castShadow>
        <boxGeometry args={[CUBE_SIZE, CUBE_SIZE, CUBE_SIZE]} />
        <meshStandardMaterial color={color} />
        <Edges scale={1.02} threshold={15} color="black" />
        <GooglyEye position={[-EYE_OFFSET_X, 0, EYE_OFFSET_Z]} lookDirection={lookDirection} pupilSize={PUPIL_SIZE * 1.2} /> {/* Slightly bigger pupil */}
        <GooglyEye position={[EYE_OFFSET_X, 0, EYE_OFFSET_Z]} lookDirection={lookDirection} pupilSize={PUPIL_SIZE * 1.2} /> {/* Slightly bigger pupil */}
         <mesh ref={leftEyebrowRef}>
            <Eyebrow position={[-EYE_OFFSET_X, EYEBROW_Y, EYEBROW_Z]} />
         </mesh>
         <mesh ref={rightEyebrowRef}>
            <Eyebrow position={[EYE_OFFSET_X, EYEBROW_Y, EYEBROW_Z]} />
         </mesh>
      </mesh>
      {/* Torso */}
      <mesh ref={torsoRef} position={[0, TORSO_Y, 0]} onClick={handleTap} castShadow>
        <boxGeometry args={[CUBE_SIZE * 1.2, CUBE_SIZE * 1.4, CUBE_SIZE]} />
        <meshStandardMaterial color={color} />
        <Edges scale={1.02} threshold={15} color="black" />
      </mesh>
      {/* Arms */}
      <mesh ref={leftArmRef} position={[-ARM_X, ARM_Y, 0]} onClick={handleTap} castShadow>
        <boxGeometry args={[CUBE_SIZE * 0.5, CUBE_SIZE * 1.2, CUBE_SIZE * 0.5]} />
        <meshStandardMaterial color={color} />
        <Edges scale={1.02} threshold={15} color="black" />
      </mesh>
      <mesh ref={rightArmRef} position={[ARM_X, ARM_Y, 0]} onClick={handleTap} castShadow>
        <boxGeometry args={[CUBE_SIZE * 0.5, CUBE_SIZE * 1.2, CUBE_SIZE * 0.5]} />
        <meshStandardMaterial color={color} />
        <Edges scale={1.02} threshold={15} color="black" />
      </mesh>
      {/* Legs */}
      <mesh ref={leftLegRef} position={[-LEG_SPACING / 2, LEG_Y, 0]} onClick={handleTap} castShadow>
        <boxGeometry args={[CUBE_SIZE * 0.6, CUBE_SIZE * 1.6, CUBE_SIZE * 0.6]} />
        <meshStandardMaterial color={color} />
        <Edges scale={1.02} threshold={15} color="black" />
      </mesh>
      <mesh ref={rightLegRef} position={[LEG_SPACING / 2, LEG_Y, 0]} onClick={handleTap} castShadow>
        <boxGeometry args={[CUBE_SIZE * 0.6, CUBE_SIZE * 1.6, CUBE_SIZE * 0.6]} />
        <meshStandardMaterial color={color} />
        <Edges scale={1.02} threshold={15} color="black" />
      </mesh>
    </group>
  );
}

// Ground component
function Ground({ isKissing }: { isKissing: boolean }) {
  const groundRef = useRef<THREE.Mesh>(null);
  const DARK_PINK_GROUND = "#c084fc"; // A darker pink/purple for ground during kiss

  useFrame(() => {
    if (groundRef.current) {
      const targetColor = isKissing ? new THREE.Color(DARK_PINK_GROUND) : new THREE.Color(PINK_COLOR);
      (groundRef.current.material as THREE.MeshStandardMaterial).color.lerp(targetColor, 0.05); // Smooth transition
    }
  });

  return (
    <RigidBody type="fixed" colliders="cuboid" restitution={0.2}>
      <Box ref={groundRef} args={[30, 1, 30]} position={[0, -0.5, 0]} receiveShadow>
        <meshStandardMaterial color={PINK_COLOR} />
        <Edges scale={1} threshold={15} color="black" />
      </Box>
    </RigidBody>
  );
}

// Main Scene Component
export default function Component() {
  const [fallingCubes, setFallingCubes] = useState<FallingCubeData[]>([]);
  const [tapCount, setTapCount] = useState(0);
  const [isKissing, setIsKissing] = useState(false);
  const [kissPhase, setKissPhase] = useState<KissPhase>(null);
  const [postKissState, setPostKissState] = useState<PostKissState>('idle');
  const [skyColor, setSkyColor] = useState<SkyColor>('blue');
  const [showBaby, setShowBaby] = useState(false);
  const [babyScale, setBabyScale] = useState(0);
  const [babyCount, setBabyCount] = useState(0);
  const [babyPositions, setBabyPositions] = useState<THREE.Vector3[]>([]);
  
  const kissStartTime = useRef(0);
  const KISS_DURATION_MOVE = 0.8; // seconds
  const KISS_DURATION_HOLD = 2.5; // seconds - longer kiss duration
  const KISS_DURATION_RETURN = 1.2; // seconds
  const BABY_APPEAR_DURATION = 1.5; // seconds for baby to fully appear

  const [char1Action, setChar1Action] = useState<CharacterAction>('walking');
  const [char2Action, setChar2Action] = useState<CharacterAction>('somersault');
  const [tapPrompt, setTapPrompt] = useState(TAP_PROMPTS[0]);
  const [lookDirection, setLookDirection] = useState(new THREE.Vector3(0, 0, 1));
  const nextCubeId = useRef(0);
  const babyAppearTime = useRef(0);

  // Make both characters the same height
  const char1InitialPos = useRef(new THREE.Vector3(-CHARACTER_SPACING / 2, 0, 0)).current;
  const char2InitialPos = useRef(new THREE.Vector3(CHARACTER_SPACING / 2, 0, 0)).current;
  const kissTargetPos1 = useRef(new THREE.Vector3(-CUBE_SIZE * 0.8, 0, 0)).current;
  const kissTargetPos2 = useRef(new THREE.Vector3(CUBE_SIZE * 0.8, 0, 0)).current;
  const babyInitialPos = useRef(new THREE.Vector3(0, -0.5, 0)).current; // Start position for baby (will be adjusted)

  const char1ActionIndex = useRef(0);
  const char2ActionIndex = useRef(0);
  const promptIndex = useRef(0);
  const justFinishedKissing = useRef(false); // Flag to track if kiss just ended

  // Cloud setup
  const numClouds = 10; // More clouds, all just moving normally
  
  // Regular clouds
  const clouds = useMemo(() => {
    return Array.from({ length: numClouds }).map((_, i) => ({
      id: i,
      initialPosition: [
        (Math.random() - 0.5) * 25, // Spread horizontally wider
        2.5 + Math.random() * 2,    // Lower Y position (closer to ground)
        -4 - Math.random() * 4      // Slightly closer Z
      ] as [number, number, number],
      speed: 0.4 + Math.random() * 0.6 // Slightly slower average speed
    }));
  }, []);


  // Kiss Animation State Machine
  useEffect(() => {
    let animationFrameId: number;
    if (isKissing) {
      const animateKiss = () => {
        const now = performance.now();
        const elapsed = (now - kissStartTime.current) / 1000;

        let currentPhase: KissPhase = null;
        if (elapsed < KISS_DURATION_MOVE) {
          currentPhase = 'movingTo';
        } else if (elapsed < KISS_DURATION_MOVE + KISS_DURATION_HOLD) {
          // Just stay in kissing phase until user taps
          currentPhase = 'kissing';
          // Change prompt to continue
          if (kissPhase !== 'kissing') {
            setTapPrompt(CONTINUE_PROMPT);
            setSkyColor('pink'); // Start changing sky to pink
          }
        } else if (elapsed < KISS_DURATION_MOVE + KISS_DURATION_HOLD + KISS_DURATION_RETURN) {
          currentPhase = 'returning';
          
          // Start baby appearance animation when characters start returning
          if (kissPhase !== 'returning') {
            setShowBaby(true);
            setBabyScale(0); // Start at zero scale
            babyAppearTime.current = now;
            
            // Add a new baby to the scene
            const newBabyCount = babyCount + 1;
            setBabyCount(newBabyCount);
            
            // Position the new baby in random places near the main characters
            // Ensure they don't overlap with existing babies
            let newPos;
            let attempts = 0;
            const maxAttempts = 20; // Increased attempts for better placement
            const minDistance = BABY_CUBE_SIZE * 2.2; // Increased minimum distance to prevent intersection

            do {
              newPos = new THREE.Vector3(
                (Math.random() - 0.5) * 5, // Wider random X position
                0, // Same height as parents
                (Math.random() - 0.5) * 4 + 2 // Random Z position in front of characters
              );
  
              // Check distance from existing babies
              const tooClose = babyPositions.some(pos => 
                pos.distanceTo(newPos) < minDistance
              );
  
              if (!tooClose || attempts >= maxAttempts) {
                // If we've tried too many times, force a safe position
                if (attempts >= maxAttempts) {
                  // Place in a grid pattern as fallback
                  const gridSize = Math.ceil(Math.sqrt(babyCount + 1));
                  const gridIndex = babyCount % (gridSize * gridSize);
                  const gridX = (gridIndex % gridSize) - gridSize/2 + 0.5;
                  const gridZ = Math.floor(gridIndex / gridSize) + 1;
                  newPos = new THREE.Vector3(
                    gridX * minDistance,
                    0,
                    gridZ * minDistance + 1
                  );
                }
                break;
              }
              attempts++;
            } while (attempts < maxAttempts);
            
            setBabyPositions(prev => [...prev, newPos]);
          }
        } else {
          setIsKissing(false);
          setKissPhase(null);
          justFinishedKissing.current = true; // Set flag
          setPostKissState('idle'); // Start in idle post-kiss state
          setSkyColor('blue'); // Return sky to blue
          // Prompt will be updated on next tap
          return;
        }

        if (currentPhase !== kissPhase) {
            setKissPhase(currentPhase);
        }
        
        // Continue animation for all phases
        animationFrameId = requestAnimationFrame(animateKiss);
      };
      
      kissStartTime.current = performance.now();
      setTapPrompt(KISS_PROMPT);
      animationFrameId = requestAnimationFrame(animateKiss);
    }
    return () => {
        if (animationFrameId) cancelAnimationFrame(animationFrameId);
    };
  }, [isKissing, kissPhase, babyCount]);

  // Baby appearance animation
  useEffect(() => {
    let animationFrameId: number;
    
    if (showBaby && babyScale < BABY_SCALE) {
      const animateBabyAppearance = () => {
        const now = performance.now();
        const elapsed = (now - babyAppearTime.current) / 1000;
        const progress = Math.min(elapsed / BABY_APPEAR_DURATION, 1);
        
        // Smooth easing function for natural growth
        const easedProgress = progress < 0.5 
          ? 2 * progress * progress 
          : 1 - Math.pow(-2 * progress + 2, 2) / 2;
        
        const newScale = easedProgress * BABY_SCALE;
        setBabyScale(newScale);
        
        if (progress < 1) {
          animationFrameId = requestAnimationFrame(animateBabyAppearance);
        }
      };
      
      animationFrameId = requestAnimationFrame(animateBabyAppearance);
    }
    
    return () => {
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
    };
  }, [showBaby, babyScale]);

  // Ensure baby cubes don't go below ground when jumping
  useEffect(() => {
    const checkBabyPositions = () => {
      setBabyPositions(prev => 
        prev.map(pos => {
          // If a baby is below ground, move it to ground level
          if (pos.y < 0) {
            return new THREE.Vector3(pos.x, 0, pos.z);
          }
          return pos;
        })
      );
    };
    
    const intervalId = setInterval(checkBabyPositions, 100);
    return () => clearInterval(intervalId);
  }, []);


  // Callback for when a character is tapped
  const handleCharacterTap = useCallback((characterId: string, worldPosition: THREE.Vector3, color: string, currentPhase: KissPhase) => {

  // --- Handle Tap During Kissing Phase ---
    if (isKissing) {
        // When tapped during kiss, immediately end the kiss and return to normal
        // Set sky back to blue
        setSkyColor('blue');
        
        // Reset prompt to the beginning of the cycle
        promptIndex.current = 0;
        setTapPrompt(TAP_PROMPTS[promptIndex.current]);
        
        // Immediately end kiss and return characters to their positions
        setIsKissing(false);
        setKissPhase('returning');
        
        // Start baby appearance animation
        setShowBaby(true);
        setBabyScale(0); // Start at zero scale
        babyAppearTime.current = performance.now();
        
        // Add a new baby to the scene
        const newBabyCount = babyCount + 1;
        setBabyCount(newBabyCount);
        
        // Position the new baby in front of the main characters
        // Ensure it doesn't overlap with existing babies
        let newPos;
        let attempts = 0;
        const maxAttempts = 20;
        const minDistance = BABY_CUBE_SIZE * 2.2; // Same increased distance as above

        do {
          newPos = new THREE.Vector3(
            (Math.random() - 0.5) * 4, // Random X position, wider spread
            0, // Same height as parents
            2 + Math.random() * 2 // Varied Z position in front of characters
          );

          // Check distance from existing babies
          const tooClose = babyPositions.some(pos => 
            pos.distanceTo(newPos) < minDistance
          );

          if (!tooClose || attempts >= maxAttempts) {
            // If we've tried too many times, force a safe position
            if (attempts >= maxAttempts) {
              // Place in a grid pattern as fallback
              const gridSize = Math.ceil(Math.sqrt(babyCount + 1));
              const gridIndex = babyCount % (gridSize * gridSize);
              const gridX = (gridIndex % gridSize) - gridSize/2 + 0.5;
              const gridZ = Math.floor(gridIndex / gridSize) + 1;
              newPos = new THREE.Vector3(
                gridX * minDistance,
                0,
                gridZ * minDistance + 1
              );
            }
            break;
          }
          attempts++;
        } while (attempts < maxAttempts);
        
        setBabyPositions(prev => [...prev, newPos]);
        
        // Force characters back to their positions
        setTimeout(() => {
          setKissPhase(null);
          // Return to previous actions without incrementing
          setChar1Action(GOLD_ACTIONS[char1ActionIndex.current]);
          setChar2Action(RED_ACTIONS[char2ActionIndex.current]);
        }, 50);
        
        return; // Don't process normal tap logic or cube shedding
    }

    // --- Handle Post-Kiss Return Interaction (After Return Phase) ---
    if (justFinishedKissing.current) {
      // On first tap after returning, just cycle actions
      char1ActionIndex.current = (char1ActionIndex.current + 1) % GOLD_ACTIONS.length;
      setChar1Action(GOLD_ACTIONS[char1ActionIndex.current]);
      char2ActionIndex.current = (char2ActionIndex.current + 1) % RED_ACTIONS.length;
      setChar2Action(RED_ACTIONS[char2ActionIndex.current]);
      
      // Reset prompt to the beginning of the cycle
      promptIndex.current = 0;
      setTapPrompt(TAP_PROMPTS[promptIndex.current]);
      
      justFinishedKissing.current = false; // Consume the flag
      return;
    }


    // --- Normal Tap Logic (Not Kissing, Not Post-Kiss Dance) ---
    const newTapCount = tapCount + 1;
    setTapCount(newTapCount);

    // Shed Cubes with correct color
    setFallingCubes(prev => {
      const newCubes: FallingCubeData[] = [];
      const baseTime = Date.now();
      for (let i = 0; i < TAP_SHED_COUNT; i++) {
        const id = nextCubeId.current++;
        const positionOffset = randomVector(0.2);
        const burstVelocity = randomVector(2.5).add(new THREE.Vector3(0, 1.5 + Math.random() * 1.5, 0));
        newCubes.push({
          id,
          position: worldPosition.clone().add(positionOffset),
          rotation: new THREE.Quaternion().setFromEuler(new THREE.Euler(Math.random() * Math.PI, Math.random() * Math.PI, Math.random() * Math.PI)),
          initialVelocity: burstVelocity,
          spawnTime: baseTime + i * 40,
          color: color, // Use the tapped character's color
        });
      }
      const combined = [...prev, ...newCubes];
      return combined.length > MAX_FALLING_CUBES ? combined.slice(combined.length - MAX_FALLING_CUBES) : combined;
    });

    // Cycle action for the tapped character
    if (characterId === 'gold') {
      char1ActionIndex.current = (char1ActionIndex.current + 1) % GOLD_ACTIONS.length;
      setChar1Action(GOLD_ACTIONS[char1ActionIndex.current]);
    } else if (characterId === 'red') {
      char2ActionIndex.current = (char2ActionIndex.current + 1) % RED_ACTIONS.length;
      setChar2Action(RED_ACTIONS[char2ActionIndex.current]);
    }

    // Cycle prompt (unless kiss is about to trigger)
    if (newTapCount < KISS_TRIGGER_COUNT) {
        promptIndex.current = (promptIndex.current + 1) % TAP_PROMPTS.length;
        setTapPrompt(TAP_PROMPTS[promptIndex.current]);
    }

    // Change eye direction randomly
    setLookDirection(new THREE.Vector3(Math.random() - 0.5, Math.random() - 0.5, 0.5).normalize());

    // Check for kiss trigger
    if (newTapCount >= KISS_TRIGGER_COUNT) {
      setTapCount(0); // Reset count
      setIsKissing(true); // Start kiss sequence
      setPostKissState('idle'); // Ensure post-kiss state starts fresh
    }

  }, [tapCount, isKissing, postKissState, justFinishedKissing, char1Action, char2Action]); // Dependencies

  // Callback to remove despawned cubes
  const handleDespawn = useCallback((id: number) => {
    setFallingCubes(prev => prev.filter(cube => cube.id !== id));
  }, []);

  // Sky color transition effect
  const skyColorRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (skyColorRef.current) {
      if (skyColor === 'pink') {
        skyColorRef.current.classList.remove('bg-gradient-to-b', 'from-sky-400', 'to-sky-600');
        skyColorRef.current.classList.add('bg-gradient-to-b', 'from-pink-300', 'to-pink-500');
      } else {
        skyColorRef.current.classList.remove('bg-gradient-to-b', 'from-pink-300', 'to-pink-500');
        skyColorRef.current.classList.add('bg-gradient-to-b', 'from-sky-400', 'to-sky-600');
      }
    }
  }, [skyColor]);

  return (
    <div ref={skyColorRef} className="relative h-screen w-full bg-gradient-to-b from-sky-400 to-sky-600 transition-colors duration-3000">
      <Canvas shadows camera={{ position: [0, 3, 10], fov: 50 }}>
        <Suspense fallback={null}>
          <ambientLight intensity={0.7} />
          <directionalLight
            position={[8, 12, 6]}
            intensity={1.4}
            castShadow
            shadow-mapSize-width={1024} shadow-mapSize-height={1024}
            shadow-camera-far={35} shadow-camera-left={-12} shadow-camera-right={12}
            shadow-camera-top={12} shadow-camera-bottom={-12}
          />
          <OrbitControls
            enablePan={false} enableZoom={true}
            minDistance={3} maxDistance={20}
            minPolarAngle={Math.PI / 4} maxPolarAngle={Math.PI / 1.5}
          />

          {/* Heart Clouds - always visible, just paused during kiss */}
          {clouds.map(cloud => (
            <Cloud 
              key={cloud.id} 
              initialPosition={cloud.initialPosition} 
              speed={cloud.speed}
              isPaused={kissPhase === 'kissing' || kissPhase === 'movingTo'} 
              isKissing={kissPhase === 'kissing'}
            />
          ))}

          <Physics gravity={[0, GRAVITY_Y, 0]}>
            <Character
              id="gold"
              color={GOLD_COLOR}
              initialPosition={char1InitialPos.toArray() as [number, number, number]}
              onCharacterTap={handleCharacterTap}
              action={char1Action}
              lookDirection={lookDirection}
              isKissing={isKissing}
              kissTargetPosition={kissTargetPos1}
              returnToPosition={char1InitialPos}
              kissPhase={kissPhase}
              postKissState={postKissState}
            />
            <Character
              id="red"
              color={RED_COLOR}
              initialPosition={char2InitialPos.toArray() as [number, number, number]}
              onCharacterTap={handleCharacterTap}
              action={char2Action}
              lookDirection={lookDirection}
              isKissing={isKissing}
              kissTargetPosition={kissTargetPos2}
              returnToPosition={char2InitialPos}
              kissPhase={kissPhase}
              postKissState={postKissState}
            />
            
            {/* Baby characters that appear after kisses - simplified to single cubes with eyes */}
            {babyPositions.map((position, index) => {
              // Adjust position to rest on ground
              const adjustedPosition = new THREE.Vector3(
                position.x,
                Math.max(0, position.y) + BABY_CUBE_SIZE * BABY_SCALE / 2, // Position so bottom edge is on ground, never below
                position.z
              );
              
              return (
                <BabyCube 
                  key={`baby-${index}`} 
                  position={adjustedPosition}
                  size={BABY_CUBE_SIZE}
                  scale={index === babyPositions.length - 1 && babyScale < BABY_SCALE ? babyScale : BABY_SCALE}
                  lookDirection={lookDirection}
                  isNewest={index === babyPositions.length - 1}
                />
              );
            })}
            {fallingCubes.map((cube) => (
              <FallingCube key={cube.id} {...cube} onDespawn={handleDespawn} />
            ))}
            <Ground isKissing={isKissing} />
          </Physics>
        </Suspense>
      </Canvas>
      {/* Tap Prompt Text Overlay */}
      <div className="absolute bottom-8 left-0 right-0 flex justify-center items-center pointer-events-none">
        <p className="text-white text-xl font-bold bg-black bg-opacity-60 px-4 py-2 rounded-lg">
          {tapPrompt}
        </p>
      </div>
    </div>
  );
}

























